import { motion } from "motion/react";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  AlertTriangle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

type BannerVariant = "error" | "success" | "warning" | "info";

interface AuthBannerProps {
  variant?: BannerVariant;
  /** Optional title rendered in bold above the body. */
  title?: string;
  /** Body content — string or rich JSX. */
  children: ReactNode;
  /** Optional inline action row (buttons, links) rendered under the body. */
  action?: ReactNode;
  /** Override the icon — pass `null` to suppress entirely. */
  icon?: LucideIcon | null;
  /** Id used by `aria-describedby` from an associated input. */
  id?: string;
}

const VARIANT_ICON: Record<BannerVariant, LucideIcon> = {
  error: AlertCircle,
  success: CheckCircle2,
  warning: AlertTriangle,
  info: Info,
};

const VARIANT_ROLE: Record<BannerVariant, "alert" | "status"> = {
  error: "alert",
  warning: "alert",
  success: "status",
  info: "status",
};

const VARIANT_ICON_COLOR: Record<BannerVariant, string> = {
  error: "var(--enforcement)",
  success: "var(--confidence)",
  warning: "var(--review)",
  info: "var(--muted-foreground)",
};

/**
 * AuthBanner — slide-down entrance, never shake.
 * Defended in research §5.6: shake reads as "you did wrong" — the wrong
 * affect for an institutional auth surface. We use a calm slide-down +
 * fade entrance over ~200ms.
 *
 * `role="alert"` on error/warning (assertive); `role="status"` on success/info
 * (polite). The `prefers-reduced-motion` user setting collapses the slide
 * to a hard cut via the global rule in styles/theme.css.
 */
export function AuthBanner({
  variant = "error",
  title,
  children,
  action,
  icon,
  id,
}: AuthBannerProps) {
  const Icon = icon === null ? null : icon || VARIANT_ICON[variant];
  const role = VARIANT_ROLE[variant];

  return (
    <motion.div
      role={role}
      id={id}
      className={`auth-banner auth-banner-${variant}`}
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0, 0, 0, 1] }}
    >
      {Icon && (
        <Icon
          className="w-4 h-4 mt-0.5 flex-shrink-0"
          style={{ color: VARIANT_ICON_COLOR[variant] }}
          aria-hidden="true"
        />
      )}
      <div className="flex-1 min-w-0">
        {title && (
          <p className="font-semibold text-sm text-[var(--foreground)] mb-0.5">
            {title}
          </p>
        )}
        <div className="text-sm text-[var(--foreground)]" style={{ lineHeight: 1.5 }}>
          {children}
        </div>
        {action && <div className="mt-2">{action}</div>}
      </div>
    </motion.div>
  );
}
